import { useEditor, useNode } from "@craftjs/core";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import SettingsTitleWrapper from "@/components/editor/shared/settings-title-wrapper";
import { ButtonColorPalete } from "@/components/editor/themes/theme-color-palette/theme-color-palette";
import { Input, Select } from "@/components/shared/forms";

import {
  ActionComponentProps,
  ACTIONS,
  getActionOrDefault,
  TAction,
} from "./action-component";

export default function ActionSettings() {
  const { topLevelBlocks } = useEditor((state) => {
    const rootNodeQuery = state.root;
    const topLevelBlocks = rootNodeQuery
      .getChildNodes()
      .map((query) => query.id)
      .map((nodeId) => {
        const node = state.getState().nodes[nodeId];
        return node;
      })
      .filter(Boolean);
    return { topLevelBlocks };
  });

  const { t } = useTranslation();

  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.props as ActionComponentProps & { colorSet: string[] },
  }));

  const handleActionTextChange = useCallback(
    (event) => setProp((props) => (props._text = event.currentTarget.value)),
    [setProp]
  );

  const handleActionExternalLinkChange = useCallback(
    (event) =>
      setProp((props) => (props.externalLink = event.currentTarget.value)),
    [setProp]
  );

  const {
    showOptions = true,
    disableLinkToForRegistrationWidget = false,
    actions = [
      ACTIONS.registrationPage,
      ACTIONS.sectionOfPage,
      ACTIONS.externalLink,
      ACTIONS.topOfPage,
      ACTIONS.bottomOfPage,
    ],
  } = props;

  // Action options
  const actionOptions: {
    label: string;
    value: TAction;
  }[] = useMemo(
    () => [
      { label: t("preference-panel.none"), value: ACTIONS.none },
      ...actions.map((action) => ({
        label: t(
          `preference-panel.${action.toLowerCase().split(/\s+/).join("-")}`
        ),
        value: action,
      })),
    ],
    [actions, t]
  );

  const actionValue = getActionOrDefault(props);

  const handleSelectActionOption = useCallback(
    (value) => {
      setProp((props) => {
        props._action = value;
        props.externalLink = "";
      });
    },
    [setProp]
  );

  // Action – Section of page
  const sectionOfPageOptions = useMemo(() => {
    return topLevelBlocks.reduce((sections, block) => {
      sections.push({
        label: t(
          "preference-panel.block-display-name." +
            block.displayName.toLowerCase()
        ),
        value: block.id,
      });

      return sections;
    }, []);
  }, [t, topLevelBlocks]);

  const sectionOfPageValue = props.scrollToSectionId;

  const handleSelectSectionOfPageOption = useCallback(
    (value) => {
      setProp((props) => (props.scrollToSectionId = value));
    },
    [setProp]
  );

  return (
    <div className="grid gap-2">
      <SettingsTitleWrapper title={t("preference-panel.action")}>
        <Input
          label={t("preference-panel.display-text")}
          name={t("preference-panel.display-text")}
          value={typeof props._text === "undefined" ? props.text : props._text}
          onChange={handleActionTextChange}
          data-testid="action-settings-component"
        />
      </SettingsTitleWrapper>

      {showOptions && (
        <>
          <Select
            name="action"
            label={t("preference-panel.link-to")}
            options={actionOptions}
            value={actionValue}
            disabled={disableLinkToForRegistrationWidget}
            onChange={handleSelectActionOption}
          />

          {actionValue === ACTIONS.sectionOfPage && (
            <div data-testid="select-section-of-page">
              <Select
                name="sectionOfPage"
                placeholder={t("preference-panel.select-an-option")}
                label={t("preference-panel.section-of-page")}
                options={sectionOfPageOptions}
                value={sectionOfPageValue}
                onChange={handleSelectSectionOfPageOption}
              />
            </div>
          )}

          {actionValue === ACTIONS.externalLink && (
            <Input
              data-testid="external-link-input"
              label=""
              name={t("preference-panel.link-to")}
              placeholder="https://www..."
              value={props.externalLink}
              onChange={handleActionExternalLinkChange}
              className="mb-4"
            />
          )}

          <ButtonColorPalete
            colorSet={props.colorSet}
            onColorChange={(colors) =>
              setProp((props) => (props.colorSet = colors))
            }
          />
        </>
      )}
    </div>
  );
}
